import { useMemo, useState } from "react";
import { useThreadRuntime } from "@assistant-ui/react";
import { ClockIcon, LoaderIcon, MapPinIcon } from "lucide-react";

import type { SlotOption, SlotPickerPayload } from "@/lib/api";
import { toBcp47 } from "@/i18n";
import { useLocale, useT } from "@/locale-context";
import { cn } from "@/lib/utils";

type DateGroup = { dateKey: string; date: Date; slots: SlotOption[] };

function groupByDate(slots: SlotOption[]): DateGroup[] {
  const map = new Map<string, DateGroup>();
  for (const slot of slots) {
    const d = new Date(slot.startUtc);
    const key = d.toDateString();
    let group = map.get(key);
    if (!group) {
      group = { dateKey: key, date: d, slots: [] };
      map.set(key, group);
    }
    group.slots.push(slot);
  }
  const populated = [...map.values()].sort(
    (a, b) => a.date.getTime() - b.date.getTime(),
  );
  if (populated.length <= 1) return populated;

  // Pad calendar gaps: the Figma shows a continuous run of date pills where
  // dates with no slots render as disabled. Fill in any missing day between
  // the earliest and latest proposed slot so the grid looks like a week view.
  const firstGroup = populated[0];
  const lastGroup = populated[populated.length - 1];
  if (!firstGroup || !lastGroup) return populated;

  const filled: DateGroup[] = [];
  const first = firstGroup.date;
  const last = lastGroup.date;
  const cursor = new Date(first.getFullYear(), first.getMonth(), first.getDate());
  const end = new Date(last.getFullYear(), last.getMonth(), last.getDate());
  while (cursor.getTime() <= end.getTime()) {
    const key = cursor.toDateString();
    const existing = populated.find((g) => g.dateKey === key);
    filled.push(existing ?? { dateKey: key, date: new Date(cursor), slots: [] });
    cursor.setDate(cursor.getDate() + 1);
  }
  return filled;
}

export function SlotPicker({ data }: { data: SlotPickerPayload }) {
  const runtime = useThreadRuntime({ optional: true });
  const [pickedSlotId, setPickedSlotId] = useState<string | null>(null);
  const [name, setName] = useState(() => data.prefill?.name?.trim() ?? "");
  const [email, setEmail] = useState(() => data.prefill?.email?.trim() ?? "");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const t = useT();
  const locale = useLocale();

  const bcp47 = toBcp47(locale);

  const groups = useMemo(() => groupByDate(data.slots), [data.slots]);
  const [selectedDateKey, setSelectedDateKey] = useState(
    groups[0]?.dateKey ?? "",
  );
  const activeGroup = groups.find((g) => g.dateKey === selectedDateKey);

  const weekdayShort = useMemo(
    () => new Intl.DateTimeFormat(bcp47, { weekday: "short" }),
    [bcp47],
  );
  const dayNum = useMemo(
    () => new Intl.DateTimeFormat(bcp47, { day: "numeric" }),
    [bcp47],
  );
  const timeFmt = useMemo(
    () => new Intl.DateTimeFormat(bcp47, { hour: "2-digit", minute: "2-digit" }),
    [bcp47],
  );
  const fullDateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(bcp47, {
        weekday: "long",
        day: "numeric",
        month: "long",
      }),
    [bcp47],
  );

  const handlePick = (slot: SlotOption) => {
    if (submitted || pending) return;
    setError(null);
    setPickedSlotId(slot.slotId);
  };

  const pickedSlot = (activeGroup?.slots ?? data.slots).find(
    (s) => s.slotId === pickedSlotId,
  );
  const canSubmit =
    pickedSlot != null && name.trim().length > 0 && email.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit || pending || submitted) return;
    if (!runtime) {
      setError(t("slotPicker.sendError"));
      return;
    }
    setError(null);
    setPending(true);
    try {
      const message =
        `\u2192 Book ${data.acceptanceType}: ${pickedSlot.label}` +
        ` — ${name.trim()} (${email.trim()})`;
      await runtime.append({
        role: "user",
        content: [{ type: "text", text: message }],
      });
      setSubmitted(true);
    } catch {
      setError(t("slotPicker.retryError"));
    } finally {
      setPending(false);
    }
  };

  const capitalise = (s: string) =>
    s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);

  return (
    <div className="my-3 flex flex-col gap-4">
      {/* ── Dealer mini-card ── */}
      <div className="rounded-[16px] border border-[#d3dfe7] bg-white px-5 py-4">
        <p className="font-bold text-[16px] text-[#353a47] leading-[1.3]">
          {data.dealer.name}
        </p>
        <p className="mt-0.5 text-[14px] text-[#353a47]/50 leading-[1.3]">
          {data.dealer.address}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px] text-[#353a47] leading-[1.3]">
          {data.dealer.distanceKm != null ? (
            <span className="inline-flex items-center gap-1">
              <MapPinIcon className="size-3.5" />
              {data.dealer.distanceKm.toFixed(1)}km
            </span>
          ) : null}
          <span className="inline-flex items-center gap-1">
            <ClockIcon className="size-3.5" />
            {data.dealer.openingHoursLabel ?? "9:00 - 5:00 PM"}
          </span>
        </div>
      </div>

      {/* ── Date pills ── */}
      {groups.length > 0 ? (
        <div>
          <div className="mb-3 flex flex-col gap-1 leading-[1.3]">
            <p className="text-[14px] text-[#353a47]">{t("slotPicker.selectDate")}</p>
            <p className="text-[14px] text-[#353a47]/50">{data.dealer.name}</p>
          </div>
          <div className="flex flex-wrap gap-[13px]">
            {groups.map((g) => {
              const isActive = g.dateKey === selectedDateKey;
              const isEmpty = g.slots.length === 0;
              return (
                <button
                  key={g.dateKey}
                  type="button"
                  disabled={isEmpty}
                  onClick={() => !isEmpty && setSelectedDateKey(g.dateKey)}
                  className={cn(
                    "flex h-[66px] flex-col items-center justify-center gap-[10px] border px-[22px] py-[16px] transition",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00c0e8]",
                    isActive
                      ? "rounded-[12px] border-[#00c0e8] bg-[#e7f3ff]"
                      : "rounded-[16px] border-[#d3dfe7] bg-white",
                    !isActive && !isEmpty && "hover:border-[#00c0e8]",
                    isEmpty && "cursor-not-allowed",
                  )}
                >
                  <span
                    className={cn(
                      "text-[14px] leading-[1.3]",
                      isEmpty
                        ? "text-[rgb(135,150,160)]"
                        : "text-[#353a47] opacity-50",
                    )}
                  >
                    {weekdayShort.format(g.date)}
                  </span>
                  <span
                    className={cn(
                      "text-[16px] font-bold leading-[1.3]",
                      isEmpty ? "text-[rgb(211,223,231)]" : "text-[#353a47]",
                    )}
                  >
                    {dayNum.format(g.date)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* ── Time slots ── */}
      <div>
        <div className="mb-3 flex flex-col gap-1 leading-[1.3]">
          <p className="text-[14px] text-[#353a47]">{t("slotPicker.availableSlots")}</p>
          {activeGroup ? (
            <p className="text-[14px] text-[#353a47]/50">
              {capitalise(fullDateFmt.format(activeGroup.date))}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-[13px]">
          {(activeGroup?.slots ?? data.slots).map((slot) => {
            const isPicked = pickedSlotId === slot.slotId;

            return (
              <button
                key={slot.slotId}
                type="button"
                aria-pressed={isPicked}
                disabled={submitted || pending}
                onClick={() => handlePick(slot)}
                className={cn(
                  "relative flex h-[44px] items-center justify-center gap-1.5 border px-[22px] py-[16px] text-[16px] font-bold leading-[1.3] transition",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00c0e8]",
                  !isPicked &&
                    "cursor-pointer rounded-[16px] border-[#d3dfe7] bg-white text-[#353a47] hover:border-[#00c0e8]",
                  isPicked &&
                    "rounded-[12px] border-[#00c0e8] bg-[#e7f3ff] text-[#353a47]",
                  slot.isSynthFallback && !isPicked && "opacity-70",
                )}
              >
                <span>{timeFmt.format(new Date(slot.startUtc))}</span>
                {slot.isSynthFallback && !isPicked ? (
                  <span className="text-[9px] leading-tight text-amber-600">
                    {t("slotPicker.synthWarning")}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Contact form ── */}
      <div className="flex flex-wrap gap-[15px]">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={submitted || pending}
          placeholder={t("slotPicker.namePlaceholder")}
          aria-label={t("slotPicker.nameLabel")}
          className="h-[44px] min-w-0 flex-1 rounded-[16px] border border-[#bbc2c7] bg-white px-[22px] text-[14px] text-[#222223] placeholder:text-[#222223]/30 focus-visible:border-[#00c0e8] focus-visible:outline-none"
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitted || pending}
          placeholder={t("slotPicker.emailPlaceholder")}
          aria-label={t("slotPicker.emailLabel")}
          className="h-[44px] min-w-0 flex-1 rounded-[16px] border border-[#bbc2c7] bg-white px-[22px] text-[14px] text-[#222223] placeholder:text-[#222223]/30 focus-visible:border-[#00c0e8] focus-visible:outline-none"
        />
      </div>

      {/* ── Submit ── */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit || pending || submitted}
        translate="no"
        style={{
          backgroundColor: !canSubmit || submitted ? "#a8e0f0" : "#00c0e8",
          color: "#ffffff",
        }}
        className={cn(
          "inline-flex h-[44px] w-full items-center justify-center gap-2 rounded-full px-4 font-medium text-[16px] transition",
          !canSubmit || submitted ? "cursor-not-allowed" : "hover:brightness-95",
        )}
      >
        <span
          aria-hidden={!pending}
          className={cn(
            "inline-flex items-center",
            pending ? "opacity-100" : "w-0 opacity-0",
          )}
        >
          <LoaderIcon className="size-4 animate-spin" />
        </span>
        <span>
          {pending
            ? t("slotPicker.submitting")
            : submitted
              ? t("slotPicker.submitted")
              : t("slotPicker.submitCta")}
        </span>
      </button>

      {/* ── Error ── */}
      {error ? (
        <p className="text-rose-600 text-xs" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
